import React, { forwardRef, useState, useEffect } from 'react';
import cn from 'classnames';
import { UseFormRegisterReturn } from 'react-hook-form';
import { useThemeContext } from '@utils/providers/theme-context.tsx';

type SwitchProps = {
  id?: string;
  name?: string;
  label?: string;
  error?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  defaultChecked?: boolean;
  checked?: boolean;
  disabled?: boolean;
  register?: UseFormRegisterReturn;
  className?: string;
  labelClass?: string;
  wrapperClass?: string;
  ariaLabel?: string;
  variant?: 'default' | 'primary';
};

const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      id,
      name,
      label,
      error,
      onChange,
      defaultChecked,
      checked,
      disabled,
      register,
      className,
      labelClass,
      wrapperClass,
      ariaLabel,
      variant: _variant = 'default',
      ...rest
    },
    ref
  ) => {
    const { theme, getColour } = useThemeContext();
    const [isChecked, setIsChecked] = useState(
      checked ?? defaultChecked ?? false
    );

    // Theme-aware colors
    const pick = (light: string, dark: string) =>
      theme === 'dark' ? dark : light;

    useEffect(() => {
      if (checked !== undefined) {
        setIsChecked(checked);
      }
    }, [checked]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newChecked = e.target.checked;
      setIsChecked(newChecked);
      onChange?.(e);
      register?.onChange?.(e);
    };

    const activeColor = getColour('activeText');

    const switchClass = cn(
      'relative inline-flex w-11 p-0.5 shrink-0 rounded-full',
      'transition-colors duration-200 ease-in-out',
      'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2',
      'md:w-8 md:p-px',
      className
    );

    const toggleClass = cn(
      // Mobile-first knob
      'size-5 rounded-full bg-white dark:bg-zinc-950 shadow-sm ring-1 ring-gray-900/5 dark:ring-white/10',
      'transition-transform duration-200 ease-in-out',
      // Translate for mobile size (w-11, p-0.5, size-5 -> ~1.25rem shift)
      isChecked ? 'translate-x-5' : 'translate-x-0',
      // At md+ fall back to your original compact geometry
      'md:size-4',
      isChecked ? 'md:translate-x-3.5' : 'md:translate-x-0'
    );

    const inputClass = cn(
      // Expand hit area a bit on mobile without changing visuals
      'absolute -inset-1 md:inset-0 appearance-none focus:outline-none',
      disabled ? 'cursor-not-allowed' : 'cursor-pointer'
    );

    const uncheckedBgColor = pick('rgb(229 231 235)', 'rgb(55 65 81)'); // gray-200 / gray-700
    const labelTextColor = pick('rgb(55 65 81)', 'rgb(229 231 235)'); // gray-700 / gray-200
    const errorTextColor = pick('rgb(239 68 68)', 'rgb(248 113 113)'); // red-500 / red-400

    return (
      <div
        className={cn(
          'flex items-center gap-x-3 md:gap-x-4 min-w-0',
          'flex-wrap',
          disabled && 'opacity-50',
          wrapperClass
        )}
      >
        <div className="flex h-6 items-center">
          <div
            className={switchClass}
            style={{
              backgroundColor: isChecked ? activeColor : uncheckedBgColor,
              boxShadow:
                '0 4px 12px 2px rgba(0,0,0,0.10), 0 2px 4px rgba(0,0,0,0.06)'
            }}
          >
            <span className={toggleClass} />
            <input
              id={id}
              name={name || register?.name}
              type="checkbox"
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ref={ref || (register?.ref as any)}
              onChange={handleChange}
              onBlur={register?.onBlur}
              checked={isChecked}
              disabled={disabled}
              aria-label={ariaLabel}
              className={inputClass}
              {...rest}
            />
          </div>
        </div>
        {label && (
          <label
            htmlFor={id}
            className={cn('text-sm/6', labelClass)}
            style={{ color: labelTextColor }}
          >
            {label}
          </label>
        )}
        {error && (
          <p className="text-sm mt-1" style={{ color: errorTextColor }}>
            {error}
          </p>
        )}
      </div>
    );
  }
);

Switch.displayName = 'Switch';

export default Switch;
